import BaseDivesController from "./base_dives_controller"

/**
 * Public dives controller — extends base for self-service booking.
 *
 * Differences from POS:
 *   - No diver search (customers enter names manually)
 *   - No inline price editing (prices are read-only)
 *   - No copy-to-all-divers (staff shortcut)
 *   - Includes shared fees section (same as POS)
 *   - Package mode: when a package is pre-populated, items are shown
 *     grouped by day, read-only, with date mapping
 *
 * Prepopulation values (from hero form redirect):
 *   prepopulatedPax (Number)       — number of divers to create
 *   prepopulatedDiveOption (String) — "category:type" to pre-select for each diver
 *   prepopulatedDates (Array)       — date strings to pre-assign
 */
export default class extends BaseDivesController {
  static values = {
    ...BaseDivesController.values,
    prepopulatedPax: { type: Number, default: 0 },
    prepopulatedDiveOption: { type: String, default: "" },
    prepopulatedDates: { type: Array, default: [] }
  }

  connect() {
    // Initialize package mode state before super.connect() calls buildInitialDivers()
    this.packageMode = { active: false, divers: {} }
    super.connect()
  }

  /**
   * Public starts with primary diver + shared fees section.
   * When prepopulated with a package, enters package mode for that diver.
   */
  buildInitialDivers() {
    const pax = this.prepopulatedPaxValue || 1
    const dates = this.prepopulatedDatesValue || []
    const diveOption = this.prepopulatedDiveOptionValue || ""

    // Find the matching product from the catalog
    let prepopProduct = null
    if (diveOption && this.productsValue) {
      prepopProduct = this.productsValue.find(p => p.value === diveOption)
    }

    // Pre-add dates to the allSelectedDates set
    dates.forEach(d => this.allSelectedDates.add(d))

    const divers = []

    for (let i = 1; i <= pax; i++) {
      const diverId = `diver_${i}`
      const diverDates = {}

      // Assign each prepopulated date with the selected product
      dates.forEach(date => {
        const products = []
        if (prepopProduct) {
          products.push({
            value: prepopProduct.value,
            title: prepopProduct.title,
            category: prepopProduct.category,
            dive_type: prepopProduct.dive_type,
            price_cents: prepopProduct.price_cents,
            qty: 1
          })
        }
        diverDates[date] = { products, addons: [], disabled: false }
      })

      divers.push({
        id: diverId,
        position: i,
        label: `Diver ${i} Full Name`,
        diverId: null,
        diverName: null,
        dates: diverDates
      })

      // Enter package mode if prepopulated product is a package
      if (prepopProduct && prepopProduct.category === "package" && prepopProduct.duration_days) {
        this._enterPackageMode(diverId, prepopProduct, dates)
      }
    }

    // Track next diver ID for subsequent addDiver calls
    this.nextDiverId = pax + 1

    // Shared fees section
    divers.push({
      id: "shared",
      position: 0,
      label: "Shared Fees",
      diverId: null,
      diverName: null,
      dates: { "__shared__": { products: [], addons: [] } }
    })

    return divers
  }

  // ===== Package Mode =====

  /**
   * Enter package mode for a diver.
   * Calculates assigned dates (first N chronologically).
   */
  _enterPackageMode(diverId, product, dates) {
    const sortedDates = [...dates].sort()
    const durationDays = product.duration_days || 0
    const assignedDates = sortedDates.slice(0, durationDays)

    this.packageMode.active = true
    this.packageMode.divers[diverId] = {
      packageValue: product.value,
      durationDays: durationDays,
      assignedDates: assignedDates
    }
  }

  /**
   * Recalculate assigned dates for a diver in package mode.
   * Called when dates are added/removed.
   */
  _recalcPackageDates(diverId) {
    const pkgInfo = this.packageMode.divers[diverId]
    if (!pkgInfo) return

    const diver = this.divers.find(d => d.id === diverId)
    if (!diver) return

    const allDates = Object.keys(diver.dates).filter(d => d !== "__shared__" && !diver.dates[d]?.disabled).sort()
    pkgInfo.assignedDates = allDates.slice(0, pkgInfo.durationDays)
  }

  /**
   * Exit package mode for a diver. Clears the package product
   * from all dates and reverts to standard booking mode.
   */
  removePackage(event) {
    event.preventDefault()
    event.stopPropagation()
    const diverId = event.currentTarget.dataset.diverKey
    const pkgInfo = this.packageMode.divers[diverId]
    if (!pkgInfo) return

    if (!confirm("Remove package? You'll be able to build your own dive plan from scratch.")) return

    const diver = this.divers.find(d => d.id === diverId)
    if (diver) {
      // Remove the package product from all dates, keep dates and addons
      Object.keys(diver.dates).forEach(date => {
        if (date === "__shared__") return
        diver.dates[date].products = diver.dates[date].products.filter(
          p => p.value !== pkgInfo.packageValue
        )
      })
    }

    delete this.packageMode.divers[diverId]
    this.packageMode.active = Object.keys(this.packageMode.divers).length > 0
    this.render()
  }

  /**
   * Override selectProductFromModal to enter package mode when a package product is selected.
   */
  selectProductFromModal(event) {
    const diverKey = event.currentTarget.dataset.diverKey
    const value = event.currentTarget.dataset.value

    // Check if the selected product is a package
    const product = this.productsValue.find(p => p.value === value)
    if (product && product.category === "package" && product.duration_days && !this._isPackageDiver(diverKey)) {
      const diver = this.divers.find(d => d.id === diverKey)
      if (diver) {
        const dates = Object.keys(diver.dates).filter(d => d !== "__shared__" && !diver.dates[d]?.disabled).sort()
        this._enterPackageMode(diverKey, product, dates)
      }
    }

    super.selectProductFromModal(event)
  }

  /**
   * Override dateToggled to recalculate package date assignments.
   *
   * Calls the base's _mutateForDateToggle directly (instead of super.dateToggled)
   * so the recalc happens before a single final render — avoiding a double
   * render (and double event dispatch) per toggle.
   */
  dateToggled(event) {
    this._mutateForDateToggle(event)

    // Recalculate assigned dates for all divers in package mode
    Object.keys(this.packageMode.divers).forEach(diverId => {
      this._recalcPackageDates(diverId)
    })

    this.render()
  }

  // ===== Package Mode Queries =====

  /**
   * Check if a diver is in package mode.
   */
  _isPackageDiver(diverId) {
    return !!this.packageMode.divers[diverId]
  }

  /**
   * Get the package info for a diver, or null.
   */
  _getPackageInfo(diverId) {
    return this.packageMode.divers[diverId] || null
  }

  /**
   * Get the package product data from the catalog for a diver in package mode.
   */
  _getPackageProduct(diverId) {
    const pkgInfo = this._getPackageInfo(diverId)
    if (!pkgInfo) return null
    return this.productsValue.find(p => p.value === pkgInfo.packageValue)
  }

  /**
   * Get the day number for a date within a diver's package assignment.
   * Returns null if the date is not an assigned package date.
   */
  _getPackageDayNumber(diverId, date) {
    const pkgInfo = this._getPackageInfo(diverId)
    if (!pkgInfo) return null
    const idx = pkgInfo.assignedDates.indexOf(date)
    return idx >= 0 ? idx + 1 : null
  }

  /**
   * Check if a date is an assigned package date for a diver.
   */
  _isAssignedPackageDate(diverId, date) {
    return this._getPackageDayNumber(diverId, date) !== null
  }
}
